// File: src/pages/LandingPage.jsx
// Description: Advanced AI-driven networking platform landing page showcasing TouchConnect's comprehensive features

import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Button, 
  Grid, 
  Card, 
  CardContent,
  Stack,
  Chip,
  useTheme,
  useMediaQuery,
  Avatar,
  IconButton,
  Tooltip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Fade,
  Slide,
  Grow,
  Hidden
} from '@mui/material';
import { Link } from 'react-router-dom';
import logo from '../assets/touchconnect_logo1.png';

// Import icons for features
import { 
  FaQrcode, 
  FaShareAlt, 
  FaUsers, 
  FaMobileAlt, 
  FaShieldAlt, 
  FaRocket,
  FaRobot,
  FaBrain,
  FaChartLine,
  FaHandshake,
  FaVideo,
  FaEnvelope,
  FaNewspaper,
  FaLightbulb,
  FaNetworkWired,
  FaWifi,
  FaUserTie,
  FaGraduationCap,
  FaBullseye,
  FaMagic,
  FaInfinity,
  FaFingerprint
} from 'react-icons/fa';

// Import Material-UI icons
import {
  AutoAwesome,
  TrendingUp,
  Psychology,
  Analytics,
  Group,
  ConnectWithoutContact,
  SmartToy,
  Insights,
  Build,
  Assessment,
  Work,
  Groups
} from '@mui/icons-material';

const LandingPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [activeFeature, setActiveFeature] = useState(0);

  // Auto-rotate features
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveFeature((prev) => (prev + 1) % 3);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const heroFeatures = [
    'Share instantly with QR & NFC + capture paper cards with OCR',
    'Save Profiles and Contacts with auto-match and one-tap invites',
    'Mentor Park: AI tools to build, align, and practice for your career'
  ];

  const coreFeatures = [
    {
      icon: <FaQrcode size={36} color={theme.palette.primary.main} />,
      title: 'QR + NFC Sharing',
      description: 'Share your profile via branded QR codes or tap-to-connect NFC—instant and memorable.',
      highlights: ['On-brand QR codes', 'NFC tag write/read', 'One-tap share']
    },
    {
      icon: <FaFingerprint size={36} color={theme.palette.secondary.main} />,
      title: 'Public Profile that Converts',
      description: 'A polished, mobile-first page at /u/yourname built for quick actions and follow-ups.',
      highlights: ['Mobile-first design', 'Action-first links', 'Update once, share everywhere']
    },
    {
      icon: <FaUsers size={36} color={theme.palette.success.main} />,
      title: 'Saved Profiles + Contacts (Paper OCR)',
      description: 'Save profiles from QR/public links, or scan paper cards with on-device OCR and auto-match.',
      highlights: ['On-device OCR prefill', 'Auto-match & convert', 'Invite to Touch Connect']
    }
  ];

  const aiFeatures = [
    {
      icon: <FaLightbulb size={28} color={theme.palette.warning.main} />,
      title: 'Personalized Growth Plans',
      description: 'AI analyzes your career and generates customized "Next Steps" growth roadmap',
      features: ['Career trajectory analysis', 'Skill gap identification', 'Industry trend insights']
    },
    {
      icon: <FaChartLine size={28} color={theme.palette.primary.main} />,
      title: 'Career Growth Forecast',
      description: 'AI predicts career trajectory, new opportunities, and emerging trends',
      features: ['Future career paths', 'Industry transitions', 'Skill development roadmap']
    },
    {
      icon: <FaNetworkWired size={28} color={theme.palette.error.main} />,
      title: 'Smart Networking Recommendations',
      description: 'AI suggests optimal connections based on goals and industry alignment',
      features: ['Strategic networking', 'Goal-based matching', 'Industry relevance']
    }
  ];

  const benefits = [
    {
      icon: <FaInfinity size={20} color={theme.palette.primary.main} />,
      text: 'One card, infinite personalized experiences for every viewer'
    },
    {
      icon: <FaBrain size={20} color={theme.palette.secondary.main} />,
      text: 'AI-driven insights that adapt your profile to each connection'
    },
    {
      icon: <FaHandshake size={20} color={theme.palette.success.main} />,
      text: 'Instant professional connections through touch and scan'
    },
    {
      icon: <FaRocket size={20} color={theme.palette.warning.main} />,
      text: 'Accelerate your career with AI-powered growth strategies'
    }
  ];

  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)' }}>
      {/* Hero Section */}
      <Container maxWidth="lg" sx={{ pt: { xs: 2, md: 4 }, pb: { xs: 3, md: 6 } }}>
        <Grid container spacing={3} alignItems="center">
          {/* Left Column - Content */}
          <Grid item xs={12} md={6}>
            <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
              {/* Logo */}
              <Box sx={{ display: 'flex', justifyContent: { xs: 'center', md: 'flex-start' }, mb: 2 }}>
                <Box
                  component="img"
                  src={logo}
                  alt="TouchConnect Logo"
                  sx={{
                    width: { xs: '80px', sm: '100px', md: '110px' },
                    height: 'auto',
                    filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.1))'
                  }}
                />
              </Box>

              {/* Main Headline */}
              <Typography 
                variant="h2" 
                gutterBottom 
                sx={{ 
                  fontWeight: 800,
                  background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 1.5,
                  fontSize: { xs: '1.9rem', sm: '2.3rem', md: '2.8rem' }
                }}
              >
                AI-Driven Professional Networking
                <br />
                <span style={{ color: theme.palette.secondary.main }}>and Mentor Park Platform</span>
              </Typography>

              {/* Rotating Subtitle */}
              <Box sx={{ mb: 2, minHeight: '44px', display: 'flex', alignItems: 'center', justifyContent: { xs: 'center', md: 'flex-start' } }}>
                <Fade in={true} timeout={1000}>
                  <Typography 
                    variant="h6" 
                    color="text.secondary" 
                    sx={{ 
                      lineHeight: 1.4,
                      maxWidth: '600px',
                      mx: { xs: 'auto', md: 0 }
                    }}
                  >
                    {heroFeatures[activeFeature]}
                  </Typography>
                </Fade>
              </Box>

              {/* Description */}
              <Typography 
                variant="body1" 
                color="text.secondary" 
                sx={{ 
                  mb: 2, 
                  lineHeight: 1.6,
                  maxWidth: '600px',
                  mx: { xs: 'auto', md: 0 },
                  fontSize: '1rem'
                }}
              >
                TouchConnect is the modern way to connect, save, and grow: QR + NFC sharing, a public profile that converts, an AI Networking Assistant, and the Mentor Park career suite — with Resume Builder, Job‑Ad Alignment, Cover Letters, Mock Interviews, and more.
              </Typography>

              {/* CTA Buttons */}
              <Stack 
                direction={{ xs: 'column', sm: 'row' }} 
                spacing={2} 
                sx={{ 
                  justifyContent: { xs: 'center', md: 'flex-start' },
                  mb: 3
                }}
              >
                <Button
                  variant="contained"
                  size="large"
                  component={Link}
                  to="/mentor-park/tools"
                  startIcon={<Build />}
                  sx={{ 
                    px: 3, 
                    py: 1.1,
                    fontSize: '1rem',
                    fontWeight: 600,
                    minWidth: '220px',
                    background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #1e3a8a 0%, #2563eb 100%)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 10px 30px rgba(30, 64, 175, 0.3)',
                    }
                  }}
                >
                  Explore Mentor Park
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  component={Link}
                  to="/register"
                  startIcon={<FaRocket />}
                  sx={{ 
                    px: 3, 
                    py: 1.1,
                    fontSize: '1rem',
                    fontWeight: 600,
                    minWidth: '220px',
                    borderWidth: '2px',
                    borderColor: theme.palette.primary.main,
                    color: theme.palette.primary.main,
                    '&:hover': {
                      borderWidth: '3px',
                      backgroundColor: theme.palette.primary.light + '20',
                    }
                  }}
                >
                  Get Started — It's Free
                </Button>
              </Stack>

              {/* Trust Indicators */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, justifyContent: { xs: 'center', md: 'flex-start' } }}>
                <Chip 
                  label="✓ Mentor Park" 
                  size="small" 
                  sx={{ backgroundColor: theme.palette.primary.light, color: 'white', fontSize: '0.75rem' }}
                />
                <Chip 
                  label="✓ AI-Powered Tools" 
                  size="small" 
                  sx={{ backgroundColor: theme.palette.secondary.light, color: 'white', fontSize: '0.75rem' }}
                />
                <Chip 
                  label="✓ Mobile Friendly" 
                  size="small" 
                  sx={{ backgroundColor: theme.palette.success.light, color: 'white', fontSize: '0.75rem' }}
                />
              </Box>
            </Box>
          </Grid>

          {/* Right Column - Interactive Demo */}
          <Grid item xs={12} md={6}>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center',
              position: 'relative'
            }}>
              {/* Main Interactive Card */}
              <Card sx={{
                width: { xs: '250px', sm: '290px', md: '330px' },
                height: { xs: '170px', sm: '190px', md: '210px' },
                background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                borderRadius: '16px',
                boxShadow: '0 15px 30px rgba(0, 0, 0, 0.1)',
                position: 'relative',
                zIndex: 2,
                transform: 'rotate(-3deg)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'rotate(-2deg) translateY(-8px)',
                  boxShadow: '0 25px 50px rgba(0, 0, 0, 0.15)',
                }
              }}>
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.25 }}>
                    <Avatar
                      sx={{
                        width: 45,
                        height: 45,
                        background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                        fontSize: '1.1rem',
                        fontWeight: 'bold'
                      }}
                    >
                      EK
                    </Avatar>
                    <Box sx={{ ml: 2 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.text.primary, fontSize: '1rem' }}>
                      Eva Kadesha
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                        AI & Networking Expert @ TouchConnect
                      </Typography>
                    </Box>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25, fontSize: '0.8rem' }}>
                    Revolutionizing professional networking with AI-powered intelligence
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip label="AI/ML" size="small" sx={{ backgroundColor: theme.palette.primary[100], fontSize: '0.7rem' }} />
                    <Chip label="Networking" size="small" sx={{ backgroundColor: theme.palette.secondary[100], fontSize: '0.7rem' }} />
                    <Chip label="Innovation" size="small" sx={{ backgroundColor: theme.palette.success[100], fontSize: '0.7rem' }} />
                  </Box>
                </CardContent>
              </Card>
              {/* Floating elements */}
              <Box sx={{
                position: 'absolute',
                top: { xs: -15, md: -20 },
                right: { xs: -15, md: -20 },
                width: { xs: '70px', md: '80px' },
                height: { xs: '70px', md: '80px' },
                background: 'white',
                borderRadius: '12px',
                boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 3,
                transform: 'rotate(12deg)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'rotate(8deg) scale(1.1)',
                }
              }}>
                <FaQrcode size={35} color={theme.palette.primary.main} />
              </Box>
              <Box sx={{
                position: 'absolute',
                bottom: { xs: -15, md: -20 },
                left: { xs: -15, md: -20 },
                width: { xs: '50px', md: '60px' },
                height: { xs: '50px', md: '60px' },
                background: 'linear-gradient(135deg, #a855f7 0%, #7c3aed 100%)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: 0.9,
                zIndex: 1,
                animation: 'pulse 2s ease-in-out infinite',
                '&:hover': {
                  transform: 'scale(1.1)',
                }
              }}>
                <FaFingerprint size={25} color="white" />
              </Box>
              <Box sx={{
                position: 'absolute',
                top: '50%',
                right: { xs: -30, md: -35 },
                transform: 'translateY(-50%)',
                width: { xs: '40px', md: '50px' },
                height: { xs: '40px', md: '50px' },
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: 0.8,
                zIndex: 1,
                animation: 'float 3s ease-in-out infinite',
              }}>
                <FaBrain size={20} color="white" />
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* Core Features (QR/NFC, Public Profile, Paper OCR + Saved) */}
      <Container maxWidth="lg" sx={{ pb: { xs: 3, md: 6 } }}>
        <Grid container spacing={2}>
          {coreFeatures.map((item, idx) => (
            <Grid item xs={12} md={4} key={idx}>
              <Card sx={{ height: '100%', border: `1px solid ${theme.palette.divider}` }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
                    {item.icon}
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>{item.title}</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    {item.description}
                  </Typography>
                  <List dense>
                    {item.highlights.map((h, i) => (
                      <ListItem key={i} sx={{ py: 0 }}>
                        <ListItemIcon>
                          <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: theme.palette.primary.main }} />
                        </ListItemIcon>
                        <ListItemText primaryTypographyProps={{ variant: 'body2' }} primary={h} />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Mentor Park Highlights */}
      <Container maxWidth="lg" sx={{ pb: { xs: 3, md: 6 } }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Typography variant="h3" sx={{ fontWeight: 700, fontSize: { xs: '1.8rem', md: '2.2rem' } }}>
            Mentor Park Highlights
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ maxWidth: '700px', mx: 'auto' }}>
            Build, align, and practice — powered by AI. Start with Career Tools & Preparation.
          </Typography>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%', border: `1px solid ${theme.palette.divider}` }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
                  <Build sx={{ color: theme.palette.primary.main }} />
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>Career Tools & Preparation</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Resume Builder, Job-Ad Alignment, Cover Letters, Mock Interviews.
                </Typography>
                <Button component={Link} to="/mentor-park/tools" variant="contained" size="small" sx={{ borderRadius: 2 }}>
                  Launch Tools
                </Button>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%', border: `1px solid ${theme.palette.divider}` }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
                  <TrendingUp sx={{ color: theme.palette.success.main }} />
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>Career Insights</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  AI-powered analysis of trajectory, next steps, and market outlook.
                </Typography>
                <Button component={Link} to="/mentor-park/career-insights" variant="outlined" size="small" sx={{ borderRadius: 2 }}>
                  See Insights
                </Button>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%', border: `1px solid ${theme.palette.divider}` }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
                  <Assessment sx={{ color: theme.palette.warning.main }} />
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>Skill Gap Analysis</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Map skills, detect gaps, and plan learning with confidence.
                </Typography>
                <Button component={Link} to="/mentor-park/skill-gap" variant="outlined" size="small" sx={{ borderRadius: 2 }}>
                  Analyze Skills
                </Button>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%', border: `1px solid ${theme.palette.divider}` }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
                  <Work sx={{ color: theme.palette.info.main }} />
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>Job Alignment</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Compare your profile to roles and tailor your next application.
                </Typography>
                <Button component={Link} to="/mentor-park/job-alignment" variant="outlined" size="small" sx={{ borderRadius: 2 }}>
                  Check Fit
                </Button>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%', border: `1px solid ${theme.palette.divider}` }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
                  <Groups sx={{ color: theme.palette.secondary.main }} />
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>Community & Networking</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Engage peers, share insights, and grow together.
                </Typography>
                <Button component={Link} to="/mentor-park/community" variant="outlined" size="small" sx={{ borderRadius: 2 }}>
                  Visit Community
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* AI-Powered Features Section */}
      <Box sx={{ 
        background: 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',
        py: { xs: 3, md: 6 },
        my: { xs: 3, md: 6 }
      }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 5 }}>
            <Typography 
              variant="h3" 
              gutterBottom 
              sx={{ 
                fontWeight: 700,
                color: theme.palette.text.primary,
                mb: 1,
                fontSize: { xs: '1.8rem', md: '2.2rem' }
              }}
            >
              AI-Powered Intelligence
            </Typography>
            <Typography 
              variant="h6" 
              color="text.secondary" 
              sx={{ maxWidth: '600px', mx: 'auto', fontSize: { xs: '1rem', md: '1.1rem' } }}
            >
              Experience the future of networking with artificial intelligence that adapts and personalizes every interaction
            </Typography>
          </Box>

          <Grid container spacing={2}>
            {aiFeatures.map((feature, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Slide direction="up" in={true} timeout={1000 + index * 200}>
                  <Card sx={{
                    height: '100%',
                    textAlign: 'center',
                    p: 2,
                    background: 'white',
                    border: `1px solid ${theme.palette.divider}`,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-6px)',
                      boxShadow: '0 15px 30px rgba(0, 0, 0, 0.1)',
                      borderColor: theme.palette.primary.main,
                    }
                  }}>
                    <CardContent>
                      <Box sx={{ mb: 2.5, display: 'flex', justifyContent: 'center' }}>
                        {feature.icon}
                      </Box>
                      <Typography 
                        variant="h6" 
                        gutterBottom 
                        sx={{ fontWeight: 600, mb: 1.25, fontSize: '1rem' }}
                      >
                        {feature.title}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ lineHeight: 1.5, mb: 2, fontSize: '0.85rem' }}
                      >
                        {feature.description}
                      </Typography>
                      <Box sx={{ textAlign: 'left' }}>
                        {feature.features.map((feat, idx) => (
                          <Box key={idx} sx={{ display: 'flex', alignItems: 'center', mb: 0.6 }}>
                            <Box sx={{ 
                              width: 6, 
                              height: 6, 
                              borderRadius: '50%', 
                              backgroundColor: theme.palette.success.main,
                              mr: 1
                            }} />
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                              {feat}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                </Slide>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Key Benefits Section */}
      <Container maxWidth="lg" sx={{ pb: { xs: 3, md: 6 } }}>
        <Box sx={{ textAlign: 'center', mb: 5 }}>
          <Typography 
            variant="h3" 
            gutterBottom 
            sx={{ 
              fontWeight: 700,
              color: theme.palette.text.primary,
              mb: 1,
              fontSize: { xs: '1.8rem', md: '2.2rem' }
            }}
          >
            Why TouchConnect is Revolutionary
          </Typography>
        </Box>

        <Grid container spacing={2}>
          {benefits.map((benefit, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Fade in={true} timeout={1000 + index * 200}>
                <Paper sx={{
                  p: 2,
                  textAlign: 'center',
                  height: '100%',
                  background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                  border: `1px solid ${theme.palette.divider}`,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 25px rgba(0, 0, 0, 0.1)',
                  }
                }}>
                  <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'center' }}>
                    {benefit.icon}
                  </Box>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      fontWeight: 500,
                      lineHeight: 1.5,
                      fontSize: '0.9rem'
                    }}
                  >
                    {benefit.text}
                  </Typography>
                </Paper>
              </Fade>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Who it's for */}
      <Container maxWidth="lg" sx={{ py: { xs: 3, md: 6 } }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Typography variant="h3" sx={{ fontWeight: 700, fontSize: { xs: '1.8rem', md: '2.2rem' } }}>
            Who it’s for
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ maxWidth: '700px', mx: 'auto' }}>
            Built for modern networking across roles and stages.
          </Typography>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%', border: `1px solid ${theme.palette.divider}` }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
                  <Work sx={{ color: theme.palette.primary.main }} />
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>Professionals</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Share a living profile, follow up smarter, and grow your network with AI.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%', border: `1px solid ${theme.palette.divider}` }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
                  <Groups sx={{ color: theme.palette.success.main }} />
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>Founders & creators</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Pitch faster, collaborate better, and build credibility with on‑brand QR/NFC.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%', border: `1px solid ${theme.palette.divider}` }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
                  <FaGraduationCap size={22} color={theme.palette.warning.main} />
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>Students & job seekers</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Build assets, align to roles, practice interviews, and turn contacts into opportunities.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* CTA Section */}
      <Box sx={{ 
        background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
        py: { xs: 3, md: 6 },
        mt: { xs: 3, md: 6 }
      }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center', color: 'white' }}>
            <Typography 
              variant="h3" 
              gutterBottom 
              sx={{ 
                fontWeight: 700,
                mb: 2,
                fontSize: { xs: '1.8rem', md: '2.2rem' }
              }}
            >
              Ready to Accelerate with Mentor Park?
            </Typography>
            <Typography 
              variant="h6" 
              sx={{ 
                mb: 3,
                opacity: 0.9,
                maxWidth: '700px',
                mx: 'auto',
                fontSize: { xs: '1rem', md: '1.1rem' }
              }}
            >
              Start with Career Tools & Preparation to generate job-ready resumes, align to job ads, and practice interviews — all AI-powered.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
              <Button
                variant="contained"
                size="large"
                component={Link}
                to="/mentor-park/tools"
                startIcon={<Build />}
                sx={{
                  px: 5,
                  py: 1.3,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  backgroundColor: 'white',
                  color: theme.palette.primary.main,
                  '&:hover': {
                    backgroundColor: theme.palette.grey[100],
                    transform: 'translateY(-2px)',
                    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
                  }
                }}
              >
                Launch Career Tools & Preparation
              </Button>
              <Button
                variant="outlined"
                size="large"
                component={Link}
                to="/register"
                startIcon={<FaUserTie />}
                sx={{
                  px: 5,
                  py: 1.3,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  borderColor: 'white',
                  color: 'white',
                  borderWidth: '2px',
                  '&:hover': {
                    borderWidth: '3px',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  }
                }}
              >
                Create Account
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>

      {/* CSS Animations */}
      <Box
        sx={{
          '@keyframes float': {
            '0%, 100%': { transform: 'translateY(0px)' },
            '50%': { transform: 'translateY(-15px)' }
          },
          '@keyframes pulse': {
            '0%, 100%': { transform: 'scale(1)' },
            '50%': { transform: 'scale(1.05)' }
          }
        }}
      />
    </Box>
  );
};

export default LandingPage;

